import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getUserDatabases, type UserDatabaseResponse } from '@/api/endpoints/userDatabases';

export const useAuthStore = defineStore('auth', () => {
    // Database state
    const userDatabases = ref<UserDatabaseResponse[] | null>(null);
    const loading = ref(false);

    // Error states - eksplisitt for ulike feiltyper
    const notAuthorized = ref(false);           // 403 - bruker finnes ikke i system
    const loadError = ref<string | null>(null); // Feilmelding for UI
    const hasServerError = ref(false);          // 5xx eller network error

    // Auth status (migrert fra storeIcs4)
    const isAuthenticated = ref(false);
    const userName = ref<string | null>(null);

    /**
     * Sett auth-status. Kalles fra auth.ts ved login/logout.
     */
    function setAuthStatus (authenticated: boolean, name: string | null): void {
        isAuthenticated.value = authenticated;
        userName.value = name;

        // Reset database state ved logout
        if (!authenticated) {
            reset();
        }
    }

    async function loadUserDatabases (): Promise<void> {
        loading.value = true;

        // Reset feil-states før nytt forsøk
        notAuthorized.value = false;
        loadError.value = null;
        hasServerError.value = false;

        try {
            userDatabases.value = await getUserDatabases();
        } catch (e: any) {
            const status = e.response?.status;

            if (!e.response) {
                // Network error - server svarer ikke
                hasServerError.value = true;
                loadError.value = 'Kunne ikke koble til server. Sjekk nettverksforbindelsen.';
                userDatabases.value = null; // null = ukjent tilstand
            } else if (status === 403) {
                // Bruker finnes ikke i system - forventet for nye brukere
                notAuthorized.value = true;
                userDatabases.value = [];
            } else if (status >= 500) {
                // Server-feil
                hasServerError.value = true;
                loadError.value = 'Serverfeil. Prøv igjen senere.';
                userDatabases.value = null;
            } else {
                // Andre feil (400, 404, etc.)
                loadError.value = e.response?.data?.title || 'Kunne ikke laste prosjekter.';
                userDatabases.value = null;
            }
        } finally {
            loading.value = false;
        }
    }

    /**
     * Prøv å laste databaser på nytt etter feil.
     */
    async function retry (): Promise<void> {
        await loadUserDatabases();
    }

    function reset (): void {
        userDatabases.value = null;
        notAuthorized.value = false;
        loadError.value = null;
        hasServerError.value = false;
    }

    return {
        // Database state
        userDatabases,
        loading,

        // Error states
        notAuthorized,
        loadError,
        hasServerError,

        // Auth status
        isAuthenticated,
        userName,

        // Actions
        setAuthStatus,
        loadUserDatabases,
        retry,
        reset
    };
});

